import { Package, CheckCircle2, Users, TrendingUp, Banknote, Clock } from 'lucide-react';
import { KpiCard } from './KpiCard';
import { ChartCard } from './ChartCard';
import { AlertsWidget } from './AlertsWidget';
import { DriversOverviewTable } from './DriversOverviewTable';
import { ShipmentTrendChart } from '../charts/ShipmentTrendChart';
import { StatusDonutChart } from '../charts/StatusDonutChart';
import { DriverRankingChart } from '../charts/DriverRankingChart';
import { HourlyDistributionChart } from '../charts/HourlyDistributionChart';
import { CodCollectionChart } from '../charts/CodCollectionChart';
import {
  useDashboardKpis,
  useShipmentTrend,
  useRevenueTrend,
  useStatusDistribution,
  useDriverRankings,
  useHourlyDistribution,
  useDriversOverview,
  useAlerts,
} from '@/hooks/useDashboardAnalytics';
import { Skeleton } from '@/components/ui/skeleton';

export function CarrierDashboard() {
  const { data: kpis, loading: kpiLoading } = useDashboardKpis('today');
  const { data: trend, loading: trendLoading } = useShipmentTrend(14);
  const { data: revenue, loading: revenueLoading } = useRevenueTrend(30);
  const { data: statusDist, loading: statusLoading } = useStatusDistribution();
  const { data: rankings, loading: rankingsLoading } = useDriverRankings(10);
  const { data: hourly, loading: hourlyLoading } = useHourlyDistribution();
  const { data: drivers, loading: driversLoading } = useDriversOverview();
  const { data: alerts, loading: alertsLoading } = useAlerts();

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid gap-3 md:gap-4 grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {kpiLoading ? (
          Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)
        ) : (
          <>
            <KpiCard
              title="شحنات اليوم"
              value={kpis?.today_shipments?.value ?? 0}
              trend={kpis?.today_shipments?.trend}
              icon={Package}
              gradient="from-blue-500 to-blue-600"
            />
            <KpiCard
              title="تسليمات اليوم"
              value={kpis?.active_deliveries?.value ?? 0}
              icon={CheckCircle2}
              gradient="from-green-500 to-green-600"
            />
            <KpiCard
              title="السائقون النشطون"
              value={kpis?.active_drivers?.value ?? 0}
              icon={Users}
              gradient="from-purple-500 to-purple-600"
            />
            <KpiCard
              title="نسبة التوصيل"
              value={kpis?.success_rate?.value ?? 0}
              format="percent"
              icon={TrendingUp}
              gradient="from-cyan-500 to-cyan-600"
              label={kpis?.success_rate?.label}
            />
            <KpiCard
              title="COD محصّل اليوم"
              value={kpis?.cod_collected?.value ?? 0}
              trend={kpis?.cod_collected?.trend}
              format="currency"
              icon={Banknote}
              gradient="from-emerald-500 to-emerald-600"
            />
            <KpiCard
              title="تحصيلات COD غير مؤكدة"
              value={kpis?.pending_settlement_amount?.count ?? 0}
              icon={Clock}
              gradient="from-orange-500 to-orange-600"
              label={
                kpis?.pending_settlement_amount?.amount > 0
                  ? `${Number(kpis?.pending_settlement_amount?.amount).toLocaleString()} إجمالي`
                  : undefined
              }
            />
          </>
        )}
      </div>

      {/* Row 1: Trend + Donut */}
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-3">
        <ChartCard title="حجم الشحنات — آخر 14 يوم" loading={trendLoading} className="lg:col-span-2">
          <ShipmentTrendChart data={trend} days={14} />
        </ChartCard>
        <ChartCard title="توزيع الحالات" loading={statusLoading}>
          <StatusDonutChart data={statusDist} />
        </ChartCard>
      </div>

      {/* Row 2: Driver Rankings + Hourly */}
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
        <ChartCard title="ترتيب السائقين — رحلات آخر 30 يوم" loading={rankingsLoading}>
          <DriverRankingChart data={rankings} />
        </ChartCard>
        <ChartCard title="توزيع التوصيلات بالساعة" loading={hourlyLoading}>
          <HourlyDistributionChart data={hourly} />
        </ChartCard>
      </div>

      {/* Row 3: COD + Drivers */}
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
        <ChartCard title="COD والرسوم — آخر 30 يوم" loading={revenueLoading}>
          <CodCollectionChart data={revenue} />
        </ChartCard>
        <DriversOverviewTable drivers={drivers} loading={driversLoading} />
      </div>

      {/* Alerts */}
      <AlertsWidget alerts={alerts} loading={alertsLoading} />
    </div>
  );
}
